import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import {debounce} from 'lodash';
import {PhoneCall} from 'lucide-react';
import {FilterChip, FilterChips, ListPanel, SearchField, Segmented, SkeletonList, useDelayedFlag} from 'ch3-ui-lib';
import {RootState, useAppDispatch} from '../../store/store';
import {getSelectedNumber, setContactsFilter, setLimit, setSelectedDates, setUsersFilter} from "../../store/features/Filters/filtersSlice";
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import {Call, useFetchHistoryListQuery, useFetchOngoingCallQuery} from "../../store/features/calls/callsService";
import {Contact, ContactSelectItem} from "../../store/features/Contacts/contactsService";
import {buildQueryParams} from "../../utilis/buildQueryParams";
import {selectUserId} from "../../store/features/Auth/authSlice";
import CallFiltersPanel from "../../components/Filters/CallFiltersPanel.tsx";
import {MissedCallData, useGetMissedCallsPerUserQuery, useResetDndNotificationMutation} from "../../store/features/Users/userService.ts";
import {
    clearMissedCallsForNumber,
    selectMissedCallsForNumber,
    selectShowMissedCallNotification,
    setHideMissedCallNotification,
    setMissedCallsForNumber
} from "../../store/features/MissedCalls/missedCallsSlice.ts";
import {activeCallFilters, allTimeRange, CallFilterKey} from "./callFilterChips";
import CallList from "./CallList";
import CallDetails from "./CallDetails";
import {isGroupCall, isMissedGroupCall, isMissedIncomingCall} from "../../utilis/callsUtilis";
import GroupCallHistory from "./GroupCallHistory.tsx";
import {useRtcFeatures} from "../../features/groupCalls/hooks/useGroupCall.ts";
import {groupCallMessages} from "../../features/groupCalls/messages.ts";
import {ActiveCallsSection} from "../../features/callSurface/calls/ActiveCallsSection";
import {useCallsPollingInterval} from "../../features/callChanges/useCallsPollingInterval";

type CallsTab = 'recent' | 'missed' | 'group';

const CallsSkeleton = ({isMobile}: { isMobile: boolean }) => (
    <SkeletonList label="Loading calls" rows={10} rowHeight={isMobile ? 56 : 48} trailing className="px-2"/>
);

export default function Calls() {
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [tab, setTab] = useState<CallsTab>('recent');
    const [search, setSearch] = useState('');
    const [phrase, setPhrase] = useState('');
    const [selectedCallId, setSelectedCallId] = useState<number | undefined>(undefined);
    const rtcFeatures = useRtcFeatures();
    const isGroupTab = tab === 'group' && rtcFeatures.groupCalls;

    const isMobile = useMediaQuery('(max-width: 960px)');
    const userId = useSelector(selectUserId);
    const historyPollingInterval = useCallsPollingInterval(15000);
    const ongoingPollingInterval = useCallsPollingInterval(5000);
    const {data: missedCallsData} = useGetMissedCallsPerUserQuery(Number(userId), {pollingInterval: historyPollingInterval});

    const {
        phoneNumbersFilter,
        contactsFilter,
        usersFilter,
        groupsFilter,
        selectedDates,
        limit,
    } = useSelector((state: RootState) => state.filters);

    const updatePhrase = useMemo(() => debounce((value: string) => setPhrase(value.trim()), 300), []);
    useEffect(() => () => updatePhrase.cancel(), [updatePhrase]);
    const onSearch = (value: string) => {
        setSearch(value);
        updatePhrase(value);
    };

    const callsQueryParams = buildQueryParams({
        limit,
        contacts: contactsFilter.map((contact: any) => contact.contactId),
        phoneNumbers: phoneNumbersFilter?.msisdn,
        groups: groupsFilter?.groupId,
        users: usersFilter.map((item: any) => item.id),
        from: selectedDates.from,
        to: selectedDates.to,
        userId,
        phrase: phrase || undefined,
    });

    const {data: historyList, isLoading} = useFetchHistoryListQuery(callsQueryParams, {pollingInterval: historyPollingInterval});
    const {data: ongoingCalls = []} = useFetchOngoingCallQuery(callsQueryParams, {pollingInterval: ongoingPollingInterval});
    const showSkeleton = useDelayedFlag(isLoading);

    const totalCount = historyList ? historyList.totalCount : 0;
    const calls = useMemo(() => historyList?.calls ?? [], [historyList]);
    const visibleCalls = useMemo(
        () => tab === 'missed'
            ? calls.filter((call) => isGroupCall(call) ? isMissedGroupCall(call) : isMissedIncomingCall(call) && !call.forwardedTo)
            : calls,
        [calls, tab],
    );
    const selectedCall = visibleCalls.find((call) => call.callId === selectedCallId) ?? visibleCalls[0];

    const handleBottomReach = useCallback(() => {
        if (totalCount > limit) {
            dispatch(setLimit(limit + 50));
        }
    }, [dispatch, limit, totalCount]);

    // Filters
    const handleContactSelect = (value: ContactSelectItem) => {
        const exists = contactsFilter.some((item: Contact) => item?.contactId === value.contactId);
        dispatch(setContactsFilter(exists
            ? contactsFilter.filter((item: Contact) => item.contactId !== value.contactId)
            : [...contactsFilter, value]));
    };

    const handleUserSelect = (value: any) => {
        const exists = usersFilter.some((item: any) => item?.id && value?.id && item.id === value.id);
        dispatch(setUsersFilter(exists
            ? usersFilter.filter((item: any) => item.userId != value?.userId || item?.groupId != value?.groupId)
            : [...usersFilter, value]));
    };

    const removeCallFilter = (key: CallFilterKey) => {
        if (key === 'contacts') dispatch(setContactsFilter([]));
        if (key === 'users') dispatch(setUsersFilter([]));
        if (key === 'date') dispatch(setSelectedDates(allTimeRange()));
    };

    const activeFilterChips: FilterChip[] = activeCallFilters({contactsFilter, usersFilter, selectedDates})
        .map(({key, label}) => ({key, label, onRemove: () => removeCallFilter(key)}));

    const clearCallFilters = () => {
        dispatch(setContactsFilter([]));
        dispatch(setUsersFilter([]));
        dispatch(setSelectedDates(allTimeRange()));
    };

    const filterHandlers = {
        onContactsChange: (contacts: any[]) => dispatch(setContactsFilter(contacts)),
        onUsersChange: (users: any[]) => dispatch(setUsersFilter(users)),
        handleContactSelect,
        handleUserSelect,
        handleDateSelect: ({from, to}: { from: string; to: string }) => dispatch(setSelectedDates({from, to})),
    };

    // Missed calls during Do Not Disturb, per line
    useEffect(() => {
        if (!missedCallsData?.data) return;
        missedCallsData.data.forEach(({phoneNumber, totalDndLastMissedCalls}: MissedCallData) => {
            dispatch(setMissedCallsForNumber({phoneNumber, count: totalDndLastMissedCalls || 0}));
        });
    }, [missedCallsData, dispatch]);

    const selectedPhoneNumber = useSelector(getSelectedNumber);
    const lineKey = selectedPhoneNumber?.msisdn?.toString() || '';
    const missedDuringDnd = useSelector(selectMissedCallsForNumber(lineKey));
    const showDndBanner = useSelector(selectShowMissedCallNotification(lineKey));
    const [resetDndNotification] = useResetDndNotificationMutation();

    const acknowledgeDndMissedCalls = useCallback(() => {
        if (!selectedPhoneNumber) return;
        resetDndNotification({userId, phoneNumberId: selectedPhoneNumber.msisdnId});
        dispatch(setHideMissedCallNotification(lineKey));
        dispatch(clearMissedCallsForNumber(lineKey));
    }, [dispatch, lineKey, resetDndNotification, selectedPhoneNumber, userId]);

    useEffect(() => {
        if (tab === 'missed') acknowledgeDndMissedCalls();
    }, [tab, acknowledgeDndMissedCalls]);

    const dndBanner = selectedPhoneNumber && missedDuringDnd?.count && showDndBanner ? {
        count: missedDuringDnd.count,
        onShow: () => setTab('missed'),
        onHide: () => dispatch(setHideMissedCallNotification(lineKey)),
    } : undefined;

    const emptyMessage = phrase
        ? `No calls match “${phrase}”`
        : tab === 'missed'
            ? 'No missed calls'
            : phoneNumbersFilter ? 'No calls on this line yet' : 'No calls yet';

    return (
        <div className="flex h-[calc(100vh-var(--header-height))] min-h-0">
            <ListPanel label="Calls" className="!w-[400px]">
                <div className="flex h-header shrink-0 items-center justify-between gap-2 px-4">
                    <Segmented
                        label="Call history"
                        value={tab}
                        onChange={setTab}
                        options={[
                            {id: 'recent', label: intl.formatMessage({id: 'calls.recent'})},
                            {id: 'missed', label: intl.formatMessage({id: 'calls.missed'})},
                            ...(rtcFeatures.groupCalls ? [{id: 'group' as const, label: intl.formatMessage(groupCallMessages.historyTab)}] : []),
                        ]}
                    />
                    {!isGroupTab && <CallFiltersPanel
                        iconOnly
                        admin={true}
                        items={['CONTACTS', 'USERS', 'DATE']}
                        handlers={filterHandlers}
                        selectedUsers={usersFilter}
                        selectedContacts={contactsFilter}
                        selectedDates={selectedDates}
                    />}
                </div>
                {isGroupTab ? (
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <GroupCallHistory/>
                    </div>
                ) : <>
                <div className="flex shrink-0 flex-col gap-2 px-4 pb-2">
                    <SearchField label="Search calls" placeholder="Search..." value={search} onChange={onSearch}/>
                    <FilterChips chips={activeFilterChips} onClearAll={clearCallFilters}/>
                </div>
                {tab === 'recent' && <ActiveCallsSection/>}
                {isLoading
                    ? (showSkeleton ? <CallsSkeleton isMobile={isMobile}/> : null)
                    : (
                        <CallList
                            calls={visibleCalls}
                            ongoingCalls={tab === 'recent' ? ongoingCalls : []}
                            selectedCall={selectedCall}
                            onSelect={(call: Call) => setSelectedCallId(call.callId)}
                            onReachEnd={handleBottomReach}
                            isMobile={isMobile}
                            emptyMessage={emptyMessage}
                            dndBanner={dndBanner}
                        />
                    )}
                </>}
            </ListPanel>
            <main aria-label="Call details" className="min-w-0 flex-1 overflow-y-auto bg-canvas">
                {isGroupTab ? null : selectedCall
                    ? <CallDetails key={selectedCall.callId} selectedCall={selectedCall}/>
                    : !isLoading && (
                        <div className="flex h-full flex-col items-center justify-center gap-3 text-callout text-ink-muted">
                            <span className="flex size-16 items-center justify-center rounded-sheet bg-surface-muted text-ink-secondary">
                                <PhoneCall size={24} aria-hidden="true" className="!p-0 !text-current"/>
                            </span>
                            Select a call to see its details
                        </div>
                    )}
            </main>
        </div>
    );
}
